import React, { useEffect, useState, useRef, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { Maximize2, Minimize2 } from 'lucide-react'
import toast from 'react-hot-toast'

import { useRoomStores } from '../stores/useRoomStores'
import useConvexSync from '../hooks/useConvexSync'
import useRoomStore from '../stores/roomStore'
import useQueueStore from '../stores/queueStore'
import usePlaybackStore from '../stores/playbackStore'
import useAudioEngine from '../hooks/useAudioEngine'
import { useSongDataPreloader } from '../hooks/useSongDataPreloader'
import usePinnedSongLyrics from '../hooks/usePinnedSongLyrics'
import ConnectionStatus from '../components/ConnectionStatus'
import SongDisplay from '../components/SongDisplay'
import LyricsDisplay from '../components/LyricsDisplay'
import PlaybackControls from '../components/PlaybackControls'
import AudioControlsModal from '../components/AudioControls'
import CanvasAnimatedBackground from '../components/CanvasAnimatedBackground'
import FloatingParticles from '../components/FloatingParticles'
import UpNextPopup from '../components/UpNextPopup'
import QRCodeComponent from '../components/QRCode'
import EmojiRainOverlay from '../components/EmojiRainOverlay'
import { extractColorsFromImage, colorToRgba, generateAnimatedBackgroundColors } from '../utils/colorExtractor'
import { getSongMediaKey } from '../lib/songMedia'
import { getActiveWordLevelLineIndex } from '../utils/wordLevelPlayback'

const BRACKET_MARKER_REGEX = /^\s*\[[^\]]+\]\s*$/
const COUNT_IN_GAP_THRESHOLD_SECONDS = 2

const toFiniteNumber = (value) => {
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : null
}

const isNonVocalMarkerLine = (text = '') => {
  const normalized = text.trim()
  if (!normalized) return false
  if (normalized === '...') return true
  return BRACKET_MARKER_REGEX.test(normalized)
}

const getPlaybackControlErrorMessage = (error, fallback) => {
  const message = String(error?.message || error || '').toLowerCase()
  if (message.includes('stale')) {
    return 'Playback is syncing. Try again.'
  }
  return fallback
}

const TVPage = () => {
  const { roomCode } = useParams()
  const {
    roomId,
    room,
    currentSong,
    pitchShift,
    cursor,
    playbackVersion, // Added versioned playback tracking
    queue,
    isConnected,
    joinRoom,
    connectRealtime,
    next: storeNext,
    previous: storePrevious,
    authoritativeIsPlaying,
    authoritativePosSeconds,
    authoritativeAnchorPosSec,
    authoritativeAnchorServerMs,
    playbackRevision,
    adjustPitch,
    processingJobs,
    loadProcessingJobs,
    getNextSong,
    emojiEvents,
    isPlaying: effectiveIsPlaying,
  } = useRoomStores()

  const [currentLyricIndex, setCurrentLyricIndex] = useState(0)
  const [lyrics, setLyrics] = useState([])
  const [lyricsReady, setLyricsReady] = useState(false)
  const [vocalVolume, setVocalVolume] = useState(0.25) // 0.0 to 2.0 (25% default)
  const [accompanimentVolume, setAccompanimentVolume] = useState(2.0) // 0.0 to 2.0 (200% default)
  const [drumsVolume, setDrumsVolume] = useState(2.0) // 0.0 to 2.0 (200% default)
  const [vocalMuted, setVocalMuted] = useState(false)
  const [accompanimentMuted, setAccompanimentMuted] = useState(false)
  const [drumsMuted, setDrumsMuted] = useState(false)
  const [pitchShiftEnabled, setPitchShiftEnabled] = useState(true)

  // Lyrics display options
  const [showLyrics, setShowLyrics] = useState(true)
  const [showTranslation, setShowTranslation] = useState(true)
  const [showRomanization, setShowRomanization] = useState(true)
  const [forceLineMode, setForceLineMode] = useState(false) // Prefer word-level display when timestamps are available
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true)

  // Visual settings for background
  const [backgroundBrightness, setBackgroundBrightness] = useState(100) // 0-100%
  const [animationPaused, setAnimationPaused] = useState(false)

  // Real-time update indicator
  const [recentUpdate, setRecentUpdate] = useState(false)

  // Up Next popup state
  const [showUpNextPopup, setShowUpNextPopup] = useState(false)
  const [upNextRemainingSeconds, setUpNextRemainingSeconds] = useState(10)

  // Removed pendingSkipAutoPlay - optimistic updates make this unnecessary
  const previousSongIdRef = useRef(null)

  // Cache management refs for tracking previous state
  const cachedSongsRef = useRef({ prev: null, current: null, next: null })
  const previousQueueStateRef = useRef(null)

  // Ref for song-ended callback to avoid re-registration spam
  const songEndedHandlerRef = useRef(null)
  const gapPreScrollKeyRef = useRef(null)


  // Mouse inactivity state
  const [controlsVisible, setControlsVisible] = useState(true)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const inactivityTimerRef = useRef(null)

  useEffect(() => {
    const updateFullscreenState = () => {
      setIsFullscreen(Boolean(document.fullscreenElement || document.webkitFullscreenElement))
    }

    document.addEventListener('fullscreenchange', updateFullscreenState)
    document.addEventListener('webkitfullscreenchange', updateFullscreenState)
    updateFullscreenState()

    return () => {
      document.removeEventListener('fullscreenchange', updateFullscreenState)
      document.removeEventListener('webkitfullscreenchange', updateFullscreenState)
    }
  }, [])

  // Dynamic gradient colors extracted from thumbnail
  const [gradientColors, setGradientColors] = useState([
    { r: 0, g: 0, b: 0 }, // Default to black
    { r: 0, g: 0, b: 0 }  // Default to black
  ])
  const [colorsLoaded, setColorsLoaded] = useState(false)

  // Animated background colors for when there's a current song
  const [animatedBackgroundColors, setAnimatedBackgroundColors] = useState(null)
  const extractedPaletteCacheRef = useRef(new Map())
  const colorExtractionRequestRef = useRef(0)

  const particleColors = useMemo(() => {
    const defaultColors = ['#486fcb', '#7ea6df', '#f5a0b9', '#fbb164', '#f15050']
    const palette = [
      ...(animatedBackgroundColors?.blobColors || []),
      ...(animatedBackgroundColors?.gradientColors || [])
    ]

    const uniquePalette = [...new Set(palette.filter(Boolean))]
    return uniquePalette.length ? uniquePalette : defaultColors
  }, [animatedBackgroundColors])

  const lyricsContainerRef = useRef(null) // For auto-scrolling lyrics

  // Audio engine hook
  const {
    pitchShifterRef,
    audioEngineReady,
    audioEngineError,
    audioTracksLoaded,
    audioLoading,
    audioError,
    currentTime,
    localIsPlaying,
    needsUserGesture,
    handlePlay: engineHandlePlay,
    handlePause: engineHandlePause,
    handleSeek,
    changePitchShiftEngine,
    setSongEndedCallback,
    handleUserGesture,
    setLyricsReadyChecker,
    preloadSong,
    invalidateCachedSong,
    getCacheStats
  } = useAudioEngine(
    currentSong,
    pitchShift,
    vocalVolume,
    accompanimentVolume,
    vocalMuted,
    accompanimentMuted,
    drumsVolume,
    drumsMuted,
    playbackVersion, // Pass version into hook
    effectiveIsPlaying,
    authoritativeAnchorPosSec,
    authoritativeAnchorServerMs,
    playbackRevision,
    'authority'  // TV is the sole audio authority
  )

  // Song data preloader for instant transitions
  const { preloadedSongData, isPreloaded } = useSongDataPreloader()

  // Reconcile currentSong from store with full data from preloader if available
  const availableCurrentSong = useMemo(() => {
    if (preloadedSongData.current && preloadedSongData.current.id === currentSong?.id) {
      console.log('🎵 Using full preloaded song data for display:', preloadedSongData.current.title)
      return preloadedSongData.current
    }
    return currentSong
  }, [currentSong, preloadedSongData.current])
  const performanceItemId = queue.find(item => item.position === cursor)?.id
  const fullCurrentSong = usePinnedSongLyrics(
    availableCurrentSong,
    `${roomCode}:${performanceItemId || currentSong?.id}:${playbackVersion}`
  )

  const upNextSong = useMemo(() => {
    const queueNext = getNextSong(queue, cursor)
    const cachedNext = preloadedSongData?.next

    if (cachedNext) {
      const queueMatchesCache = queueNext?.songId === cachedNext.id

      const metadata = {
        songId: cachedNext.id ?? (queueMatchesCache ? queueNext.songId : null),
        id: cachedNext.id ?? (queueMatchesCache ? queueNext.id : undefined),
        title: cachedNext.title ?? (queueMatchesCache ? queueNext.song?.title : null),
        artist: cachedNext.artist ?? (queueMatchesCache ? queueNext.song?.artist : null),
        youtubeId: cachedNext.youtubeId ?? (queueMatchesCache ? queueNext.song?.youtubeId : null),
        song: queueMatchesCache
          ? queueNext.song
          : {
              id: cachedNext.id,
              title: cachedNext.title,
              artist: cachedNext.artist,
              youtubeId: cachedNext.youtubeId,
            },
      }

      return queueMatchesCache
        ? {
          ...queueNext,
          ...metadata
        }
        : metadata
    }

    return queueNext || null
  }, [cursor, getNextSong, preloadedSongData, queue])

  const currentSongId = currentSong?.id || null
  const currentSongYoutubeId = currentSong?.youtubeId || null
  const thumbnailUrl = currentSongYoutubeId
    ? `/uploads/songs/${currentSongYoutubeId}/thumbnail.webp`
    : null

  // Debug current state and real-time updates
  useEffect(() => {
    console.log('🔍 TV Page State Update:', {
      currentSong: currentSong?.title || 'None',
      cursor: cursor,
      localIsPlaying,
      currentTime,
      isConnected,
      queueLength: queue.length,
      queueItems: queue.map(item => ({
        id: item.id,
        title: item.song?.title,
        status: item.status,
        position: item.position,
        isAtCursor: item.position === cursor
      })),
      connectionHealth: (() => {
        try {
          const health = useRoomStore.getState().getConnectionHealth()
          return {
            healthy: health.healthy,
            stale: health.stale,
            autoReconnectEnabled: health.autoReconnectEnabled,
            isReconnecting: health.isReconnecting
          }
        } catch (e) {
          return 'Error getting health'
        }
      })()
    })
  }, [currentSong, cursor, localIsPlaying, isConnected, queue]) // Removed currentTime to avoid excessive logging

  // Specific queue change logging for real-time verification
  useEffect(() => {
    const now = new Date()
    const nextSong = queue.find(item => item.status === 'queued')
    const playingSong = queue.find(item => item.status === 'playing')

    console.log('🎵 TV Page - Queue Updated:', {
      timestamp: now.toLocaleTimeString(),
      totalItems: queue.length,
      playingCount: queue.filter(item => item.status === 'playing').length,
      queuedCount: queue.filter(item => item.status === 'queued').length,
      playedCount: queue.filter(item => item.status === 'played').length,
      currentSong: playingSong ? {
        title: playingSong.song?.title,
        position: playingSong.position
      } : null,
      nextSong: nextSong ? {
        title: nextSong.song?.title,
        artist: nextSong.song?.artist,
        position: nextSong.position
      } : null,
      allSongs: queue.map(item => ({
        position: item.position,
        title: item.song?.title,
        status: item.status
      })).sort((a, b) => a.position - b.position)
    })

    // Show visual update indicator
    setRecentUpdate(true)

    // Clear indicator after 2 seconds
    const timer = setTimeout(() => {
      setRecentUpdate(false)
    }, 2000)

    return () => clearTimeout(timer)
  }, [queue])

  // Track cursor changes (for skip/rewind debugging)
  useEffect(() => {
    console.log('🎯 TV Page - Cursor Changed:', {
      timestamp: new Date().toLocaleTimeString(),
      newCursor: cursor,
      currentSongTitle: currentSong?.title || 'None',
      queueAtCursor: queue.find(item => item.position === cursor)?.song?.title || 'None'
    })
  }, [cursor])

  // Removed manual skip auto-play logic - optimistic updates handle this automatically


  // Convex real-time sync (replaces manual connectRealtime + loadProcessingJobs)
  const { isLoading: convexLoading, roomNotFound } = useConvexSync(roomCode)

  // Initialize room connection
  useEffect(() => {
    console.log('🚀 Initializing TV room:', roomCode)
    joinRoom(roomCode)

    const handleBeforeUnload = () => {
      if (pitchShifterRef.current) {
        pitchShifterRef.current.destroy()
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      console.log('🧹 Cleaning up TV page')
      window.removeEventListener('beforeunload', handleBeforeUnload)
      useRoomStore.getState().disconnectRealtime()
    }
  }, [roomCode])

  // Manual control wrappers that preserve playback state
  const handleNext = async () => {
    console.log('🎵 Manual next requested', { isPlaying: effectiveIsPlaying });
    await storeNext();
  };

  const handlePrevious = async () => {
    console.log('🎵 Manual previous requested', { isPlaying: effectiveIsPlaying });
    await storePrevious();
  };

  const handlePlay = async () => {
    console.log('▶️ Global Play requested');
    if (needsUserGesture) {
      await handleUserGesture();
    }
    // Direct engine control — immediate, no roundtrip
    await engineHandlePlay();
    // Fire-and-forget to server so participants can follow
    usePlaybackStore.getState().play(roomId, currentTime).catch((error) => {
      console.error('Failed to sync play to server:', error);
    });
  };

  const handlePause = async () => {
    console.log('⏸️ Global Pause requested');
    // Direct engine control — immediate, no roundtrip
    await engineHandlePause();
    // Fire-and-forget with accurate client position
    const position = pitchShifterRef.current?.getCurrentTime() ?? currentTime;
    usePlaybackStore.getState().pause(roomId, position).catch((error) => {
      console.error('Failed to sync pause to server:', error);
    });
  };

  // Set up song ended callback for auto-advance
  // Update song-ended handler ref with latest state/functions
  // This allows the handler to access current values without re-registering
  useEffect(() => {
    songEndedHandlerRef.current = async () => {
      console.log('🎵 Song ended - resolving authoritative auto-advance');

      // Get current state
      const { queue } = useQueueStore.getState();
      const { cursor, currentSong } = usePlaybackStore.getState();
      const clearPlaybackIfQueueExhausted = (c) => usePlaybackStore.getState().clearPlaybackIfQueueExhausted(queue, c);

      // Determine the current cursor position, falling back to the playing item's position if needed
      const activeCursor =
        typeof cursor === 'number'
          ? cursor
          : (queue.find(item => item.status === 'playing')?.position ?? 0);

      const endedQueueItemId = queue.find(item => item.position === activeCursor)?.id ?? null;

      console.log('🎵 Auto-advance check:', {
        queueLength: queue.length,
        cursor: activeCursor,
        endedQueueItemId,
        currentSongTitle: currentSong?.title || 'None',
        queueItems: queue.map(item => ({
          title: item.song?.title,
          status: item.status || 'undefined'
        }))
      });

      try {
        const result = await usePlaybackStore.getState().songEnded(roomId, endedQueueItemId);
        console.log('🎵 songEnded command result:', result);

        // Duplicate or stale end events are ignored by the server.
        if (result?.stale) {
          return;
        }

        // Queue exhausted: server already paused playback; clear local empty-state presentation.
        if (result?.success && result?.advanced === false) {
          clearPlaybackIfQueueExhausted(activeCursor);
        }
      } catch (error) {
        console.error('🎵 Failed to process song-ended auto-advance:', error);
      }
    };
  });

  // Register song-ended callback with audio engine (only once when ready)
  useEffect(() => {
    if (audioEngineReady && setSongEndedCallback) {
      // Stable wrapper that calls the ref's current value
      const stableCallback = () => {
        songEndedHandlerRef.current?.();
      };

      setSongEndedCallback(stableCallback);
      console.log('🎵 Song ended callback registered for auto-advance');

      // Cleanup on unmount
      return () => {
        setSongEndedCallback(null);
        console.log('🎵 Song ended callback cleared on unmount');
      };
    }
  }, [audioEngineReady, setSongEndedCallback]);

  // Set up lyrics readiness checker for audio engine integration
  useEffect(() => {
    if (setLyricsReadyChecker) {
      setLyricsReadyChecker(() => lyricsReady)
    }
  }, [setLyricsReadyChecker, lyricsReady])

  // Note: Auto-play is now handled elegantly by the audio engine's reconciliation feature
  // The engine checks both audio AND lyrics readiness before auto-playing

  // Monitor audioTracksLoaded changes for debugging
  useEffect(() => {
    console.log('🎵 📊 AudioTracksLoaded changed:', {
      audioTracksLoaded,
      currentSong: currentSong?.title || 'None',
      timestamp: new Date().toLocaleTimeString()
    });

    // If audioTracksLoaded goes false unexpectedly while we have a song, log the stack trace
    if (!audioTracksLoaded && currentSong && audioEngineReady) {
      console.warn('🎵 ⚠️ AudioTracksLoaded went false unexpectedly!');
      console.trace('Stack trace for audioTracksLoaded = false');
    }
  }, [audioTracksLoaded]);

  // Note: Removed aggressive state sync logic that was causing play/pause button flashing
  // TV page is single-instance per room, so local audio state is the only source of truth

  // Up Next popup timing logic - show popup in last 10 seconds (when controls are hidden)
  useEffect(() => {
    if (!currentSong || !currentSong.duration || !localIsPlaying) {
      if (showUpNextPopup) {
        setShowUpNextPopup(false)
      }
      return
    }

    // If controls are visible, hide the countdown popup (component will show controls version instead)
    if (controlsVisible) {
      if (showUpNextPopup) {
        setShowUpNextPopup(false)
      }
      return
    }

    // Check if there's a next song available
    const upcomingSong = useQueueStore.getState().getNextSong(cursor)

    if (!upcomingSong) {
      if (showUpNextPopup) {
        setShowUpNextPopup(false)
      }
      return
    }

    const remainingTime = currentSong.duration - currentTime
    const shouldShowPopup = remainingTime <= 10 && remainingTime > 0

    // Only update state when there's actually a change
    if (shouldShowPopup && !showUpNextPopup) {
      setShowUpNextPopup(true)
      console.log(`🎵 Up Next popup showing - remaining: ${remainingTime.toFixed(1)}s`)
    } else if (!shouldShowPopup && showUpNextPopup) {
      setShowUpNextPopup(false)
      console.log(`🎵 Up Next popup hiding - remaining: ${remainingTime.toFixed(1)}s`)
    }

    // Update remaining seconds for countdown (only when popup is visible)
    if (shouldShowPopup && showUpNextPopup) {
      setUpNextRemainingSeconds(Math.max(0, remainingTime))
    }

  }, [currentTime, currentSong, localIsPlaying, queue, cursor, controlsVisible, showUpNextPopup])

  // Sync lyrics index and scrolling with currentTime from audio engine
  useEffect(() => {
    if (lyrics.length === 0) return;
    if (!audioTracksLoaded) {
      if (currentLyricIndex !== 0) {
        setCurrentLyricIndex(0);
      }
      if (autoScrollEnabled && lyricsContainerRef.current && lyricsContainerRef.current.scrollTop > 1) {
        lyricsContainerRef.current.scrollTo({
          top: 0,
          behavior: 'auto'
        });
      }
      return;
    }

    const wordLevel = fullCurrentSong?.wordLevelTranscription;
    const useWordLevelTiming = !forceLineMode && wordLevel && wordLevel.length > 0;
    const animationOffset = 0.5; // Start showing next line/word 500ms early
    const firstWordLine = useWordLevelTiming
      ? wordLevel.find((line) => line.segments && line.segments.length > 0)
      : null;
    const rawFirstLyricStart = firstWordLine?.segments?.[0]?.start_time ?? lyrics[0]?.start_time ?? 0;
    const firstLyricStart = Number.isFinite(Number(rawFirstLyricStart))
      ? Number(rawFirstLyricStart)
      : 0;
    const preRollThreshold = Math.max(0, firstLyricStart - animationOffset);

    // Keep lyrics pinned to the top before the first line starts.
    if (currentTime < preRollThreshold) {
      if (currentLyricIndex !== 0) {
        setCurrentLyricIndex(0);
      }
      if (autoScrollEnabled && lyricsContainerRef.current && lyricsContainerRef.current.scrollTop > 1) {
        lyricsContainerRef.current.scrollTo({
          top: 0,
          behavior: 'auto'
        });
      }
      return;
    }

    let newIndex = -1;

    if (useWordLevelTiming) {
      // Lines can overlap when backing vocals or the next phrase start early.
      // Follow the line whose first word started most recently.
      newIndex = getActiveWordLevelLineIndex(wordLevel, currentTime);
    } else {
      // Line-level mode: behavior using line start times
      for (let i = 0; i < lyrics.length; i++) {
        const lyric = lyrics[i];
        const adjustedStartTime = lyric.start_time - animationOffset;

        if (currentTime >= adjustedStartTime) {
          if (i === lyrics.length - 1) {
            newIndex = i;
          } else {
            const nextLyric = lyrics[i + 1];
            const nextAdjustedStartTime = nextLyric.start_time - animationOffset;
            if (currentTime < nextAdjustedStartTime) {
              newIndex = i;
              break;
            }
          }
        }
      }
    }

    let preScrollTargetIndex = null

    if (newIndex !== -1) {
      if (useWordLevelTiming) {
        const line = wordLevel?.[newIndex]
        const nextLine = wordLevel?.[newIndex + 1]
        const segments = line?.segments || []
        const nextSegments = nextLine?.segments || []
        const hasUpcomingLine = nextSegments.length > 0

        if (hasUpcomingLine) {
          const isInstrumentalLine = segments.length === 0
          const lineEnd = toFiniteNumber(segments[segments.length - 1]?.end_time)
          const nextLineStart = toFiniteNumber(nextSegments[0]?.start_time)
          const hasLongGapToNext = (
            Number.isFinite(lineEnd) &&
            Number.isFinite(nextLineStart) &&
            (nextLineStart - lineEnd) > COUNT_IN_GAP_THRESHOLD_SECONDS
          )

          const shouldShowGapCountIn = isInstrumentalLine || hasLongGapToNext
          const hasLineEnded = Number.isFinite(lineEnd) ? currentTime >= lineEnd : false
          const isBeforeNextLineStart = Number.isFinite(nextLineStart) ? currentTime < nextLineStart : false

          if (shouldShowGapCountIn && hasLineEnded && isBeforeNextLineStart) {
            preScrollTargetIndex = newIndex + 1
          }
        }
      } else {
        const lyric = lyrics[newIndex]
        const nextLyric = lyrics[newIndex + 1]
        const lyricEndTime = toFiniteNumber(lyric?.end_time)
        const nextLyricStartTime = toFiniteNumber(nextLyric?.start_time)
        const isBracketMarker = isNonVocalMarkerLine(lyric?.text || '')
        const hasLongGapToNext = (
          Number.isFinite(lyricEndTime) &&
          Number.isFinite(nextLyricStartTime) &&
          (nextLyricStartTime - lyricEndTime) > COUNT_IN_GAP_THRESHOLD_SECONDS
        )
        const shouldShowGapCountIn = Number.isFinite(nextLyricStartTime) && (isBracketMarker || hasLongGapToNext)
        const hasLineEnded = Number.isFinite(lyricEndTime) ? currentTime >= lyricEndTime : false
        const isBeforeNextLineStart = Number.isFinite(nextLyricStartTime) ? currentTime < nextLyricStartTime : false

        if (shouldShowGapCountIn && hasLineEnded && isBeforeNextLineStart) {
          preScrollTargetIndex = newIndex + 1
        }
      }
    }

    let didPreScrollNow = false

    if (autoScrollEnabled && Number.isInteger(preScrollTargetIndex)) {
      const preScrollKey = `${newIndex}->${preScrollTargetIndex}`
      if (gapPreScrollKeyRef.current !== preScrollKey) {
        scrollToCurrentLyric(preScrollTargetIndex)
        gapPreScrollKeyRef.current = preScrollKey
        didPreScrollNow = true
      }
    } else {
      gapPreScrollKeyRef.current = null
    }

    // Pre-scrolling only moves the viewport. The active line must continue to
    // represent playback, otherwise the next line highlights before it starts.
    if (newIndex !== -1 && newIndex !== currentLyricIndex) {
      setCurrentLyricIndex(newIndex);
      if (autoScrollEnabled && !didPreScrollNow) {
        scrollToCurrentLyric(newIndex);
      }
    }
  }, [currentTime, lyrics, fullCurrentSong?.wordLevelTranscription, forceLineMode, currentLyricIndex, audioTracksLoaded, autoScrollEnabled]);

  // Forced line mode should always start from the top of the lyrics list.
  useEffect(() => {
    if (!forceLineMode) return;

    setCurrentLyricIndex(0);

    if (autoScrollEnabled && lyricsContainerRef.current) {
      lyricsContainerRef.current.scrollTo({
        top: 0,
        behavior: 'auto'
      });
    }
  }, [forceLineMode, fullCurrentSong?.id, autoScrollEnabled]);

  // Update lyrics when current song changes
  useEffect(() => {
    console.log('🎵 currentSong/fullCurrentSong useEffect triggered:', {
      currentSong: currentSong?.title || 'null',
      hasLyrics: fullCurrentSong?.lyrics ? fullCurrentSong.lyrics.length : 0
    });

    // Reset lyrics ready state when song changes
    setLyricsReady(false);

    if (fullCurrentSong && fullCurrentSong.lyrics) {
      console.log('🎵 Raw lyrics data:', fullCurrentSong.lyrics.slice(0, 3)) // Log first 3 for debugging

      // Process lyrics to ensure all timestamps are in seconds (numeric format)
      const processedLyrics = fullCurrentSong.lyrics.map((lyric, index) => {
        const originalStart = lyric.start_time
        const originalEnd = lyric.end_time
        const processedLyric = {
          ...lyric,
          start_time: parseTimeToSeconds(lyric.start_time),
          end_time: parseTimeToSeconds(lyric.end_time)
        }

        // Log conversion for debugging (first few lyrics)
        if (index < 3) {
          console.log(`🎵 Lyric ${index}: "${lyric.text}"`)
          console.log(`   Start: ${originalStart} -> ${processedLyric.start_time}s`)
          console.log(`   End: ${originalEnd} -> ${processedLyric.end_time}s`)
        }

        return processedLyric
      })

      setLyrics(processedLyrics)
      setCurrentLyricIndex(0)
      gapPreScrollKeyRef.current = null
      setLyricsReady(true)

      // Scroll to top of lyrics container when new song loads
      if (lyricsContainerRef.current) {
        lyricsContainerRef.current.scrollTo({
          top: 0,
          behavior: 'auto' // Instant for initial load, no animation needed
        })
      }

      console.log('🎵 ✅ Lyrics processed and ready for display')
    } else {
      setLyrics([])
      setCurrentLyricIndex(0)
      gapPreScrollKeyRef.current = null
      setLyricsReady(true) // Ready even if no lyrics
    }
  }, [fullCurrentSong])

  // 3-Song Cache Management: Preload prev/next songs and handle queue changes
  useEffect(() => {
    if (!audioEngineReady || !queue.length || !preloadSong) return

    // Get helper functions from store
    const { getPreviousSong, getNextSong } = useQueueStore.getState()

    // Identify previous and next songs based on current cursor
    const prevSong = getPreviousSong(queue, cursor)
    const nextSong = getNextSong(queue, cursor)

    // Current state
    const currentState = {
      prev: getSongMediaKey(prevSong),
      current: getSongMediaKey(currentSong),
      next: getSongMediaKey(nextSong)
    }

    // Previous state for comparison
    const previousState = cachedSongsRef.current

    console.log('🎵 📦 Cache check:', {
      cursor,
      currentState,
      previousState,
      queueLength: queue.length
    })

    // Detect changes and handle cache invalidation
    if (currentState.prev !== previousState.prev && previousState.prev) {
      console.log(`🎵 📦 Previous song changed: ${previousState.prev} -> ${currentState.prev}`)
      invalidateCachedSong(previousState.prev)
    }

    if (currentState.next !== previousState.next && previousState.next) {
      console.log(`🎵 📦 Next song changed: ${previousState.next} -> ${currentState.next}`)
      invalidateCachedSong(previousState.next)
    }

    // Preload new previous song if needed
    if (prevSong && currentState.prev !== previousState.prev) {
      console.log(`🎵 📦 Preloading previous song: ${prevSong.song?.title}`)
      usePlaybackStore.getState().getSongData(prevSong.songId)
        .then(songData => preloadSong(songData))
        .catch(error => console.warn('Failed to load previous song for preloading:', error))
    }

    // Preload new next song if needed
    if (nextSong && currentState.next !== previousState.next) {
      console.log(`🎵 📦 Preloading next song: ${nextSong.song?.title}`)
      usePlaybackStore.getState().getSongData(nextSong.songId)
        .then(songData => preloadSong(songData))
        .catch(error => console.warn('Failed to load next song for preloading:', error))
    }

    // Update cached state reference
    cachedSongsRef.current = currentState

    // Log cache stats for debugging
    const stats = getCacheStats()
    if (stats) {
      console.log('🎵 📦 Cache stats:', stats)
    }

  }, [queue, cursor, currentSong, audioEngineReady, preloadSong, invalidateCachedSong, getCacheStats])

  // Extract colors from thumbnail when song changes
  useEffect(() => {
    const requestId = ++colorExtractionRequestRef.current

    const applyPalette = (colors) => {
      setGradientColors(colors)
      setAnimatedBackgroundColors(generateAnimatedBackgroundColors(colors))
      setColorsLoaded(true)
    }

    const extractThumbnailColors = async () => {
      if (thumbnailUrl && currentSongYoutubeId) {
        const cached = extractedPaletteCacheRef.current.get(currentSongYoutubeId)
        if (cached) {
          applyPalette(cached)
          return
        }

        console.log('🎨 Extracting colors from thumbnail:', thumbnailUrl)
        setColorsLoaded(false)

        try {
          const colors = await extractColorsFromImage(thumbnailUrl)
          if (requestId !== colorExtractionRequestRef.current) return
          extractedPaletteCacheRef.current.set(currentSongYoutubeId, colors)
          applyPalette(colors)

          console.log('🎨 ✅ Colors extracted successfully:', {
            color1: `rgb(${colors[0].r}, ${colors[0].g}, ${colors[0].b})`,
            color2: `rgb(${colors[1].r}, ${colors[1].g}, ${colors[1].b})`,
            animatedColors: generateAnimatedBackgroundColors(colors),
            thumbnailUrl
          })
        } catch (error) {
          if (requestId !== colorExtractionRequestRef.current) return
          console.warn('🎨 Failed to extract colors, using defaults:', error)
          // Use default contrasting colors on failure
          const defaultColors = [
            { r: 59, g: 130, b: 246 }, // Blue
            { r: 147, g: 51, b: 234 }  // Purple
          ]
          applyPalette(defaultColors)
        }
      } else {
        // No thumbnail available, use default colors
        if (requestId !== colorExtractionRequestRef.current) return
        setGradientColors([
          { r: 0, g: 0, b: 0 },  // Black
          { r: 0, g: 0, b: 0 }   // Black
        ])
        setColorsLoaded(true)
        console.log('🎨 No thumbnail available, using default black gradient')
      }
    }

    extractThumbnailColors()
  }, [currentSongId, currentSongYoutubeId, thumbnailUrl])

  // Mouse activity tracking for auto-hiding controls
  useEffect(() => {
    const resetInactivityTimer = () => {
      // Show controls
      setControlsVisible(true)

      // Clear existing timer
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current)
      }

      // Set new timer for 5 seconds
      inactivityTimerRef.current = setTimeout(() => {
        setControlsVisible(false)
      }, 5000)
    }

    const handleMouseMove = () => resetInactivityTimer()
    const handleMouseClick = () => resetInactivityTimer()
    const handleKeyDown = () => resetInactivityTimer()
    const handleTouchStart = () => resetInactivityTimer()

    // Add event listeners for user activity
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mousedown', handleMouseClick)
    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('touchstart', handleTouchStart)

    // Set initial timer
    resetInactivityTimer()

    // Cleanup
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mousedown', handleMouseClick)
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('touchstart', handleTouchStart)

      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current)
      }
    }
  }, [])

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Convert time format to seconds (handles both numeric and string formats)
  const parseTimeToSeconds = (timeValue) => {
    // If it's already a number, return it
    if (typeof timeValue === 'number') {
      return timeValue
    }

    // If it's a string, parse it
    if (typeof timeValue === 'string') {
      // Handle format like "1:12" (1 minute 12 seconds = 72 seconds)
      if (timeValue.includes(':')) {
        const parts = timeValue.split(':')
        if (parts.length === 2) {
          const minutes = parseInt(parts[0], 10)
          const seconds = parseFloat(parts[1])
          return minutes * 60 + seconds
        }
        // Handle format like "1:12:30" (1 hour 12 minutes 30 seconds)
        if (parts.length === 3) {
          const hours = parseInt(parts[0], 10)
          const minutes = parseInt(parts[1], 10)
          const seconds = parseFloat(parts[2])
          return hours * 3600 + minutes * 60 + seconds
        }
      }

      // Handle pure numeric string like "72.5"
      const numericValue = parseFloat(timeValue)
      if (!isNaN(numericValue)) {
        return numericValue
      }
    }

    // Fallback to 0 if we can't parse it
    console.warn('🚨 Could not parse time value:', timeValue)
    return 0
  }

  const getProgressPercentage = () => {
    if (!currentSong || !currentSong.duration) return 0
    return (currentTime / currentSong.duration) * 100
  }

  const getKeyDisplay = () => {
    if (!currentSong || !currentSong.originalKey) return ''

    const keys = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B']
    const originalIndex = keys.indexOf(currentSong.originalKey)

    if (originalIndex === -1) return currentSong.originalKey

    const newIndex = (originalIndex + pitchShift + 12) % 12
    return keys[newIndex]
  }

  // Volume control functions
  const handleVocalVolumeChange = (newVolume) => {
    setVocalVolume(newVolume)
  }

  const handleAccompanimentVolumeChange = (newVolume) => {
    setAccompanimentVolume(newVolume)
  }

  const handleDrumsVolumeChange = (newVolume) => {
    setDrumsVolume(newVolume)
  }

  const toggleVocalMute = () => {
    setVocalMuted(!vocalMuted)
  }

  const toggleAccompanimentMute = () => {
    setAccompanimentMuted(!accompanimentMuted)
  }

  const toggleDrumsMute = () => {
    setDrumsMuted(!drumsMuted)
  }

  const handlePitchShiftEnabledChange = async (enabled) => {
    const changed = await changePitchShiftEngine(enabled ? 'signalsmith' : 'bypass')
    if (changed) {
      setPitchShiftEnabled(enabled)
    } else {
      toast.error('Unable to change the pitch-shifting mode in this browser.')
    }
  }

  const handleFullscreenToggle = async () => {
    try {
      if (document.fullscreenElement || document.webkitFullscreenElement) {
        if (document.exitFullscreen) {
          await document.exitFullscreen()
        } else {
          document.webkitExitFullscreen?.()
        }
      } else if (document.documentElement.requestFullscreen) {
        await document.documentElement.requestFullscreen()
      } else {
        document.documentElement.webkitRequestFullscreen?.()
      }
    } catch (error) {
      console.error('Unable to change fullscreen mode:', error)
      toast.error('Fullscreen is unavailable in this browser.')
    }
  }

  // Pitch shifting functions - real-time adjustment without reloading audio
  const handlePitchShiftChange = async (newPitchShift) => {
    console.log(`🎵 Real-time pitch shift change: ${pitchShift} -> ${newPitchShift}`)

    try {
      // Update the store (existing functionality) - this calls the API
      await adjustPitch(newPitchShift)
      console.log(`🎵 Pitch shift API call successful: ${newPitchShift}`)
    } catch (error) {
      console.error('🎵 Failed to adjust pitch:', error)
    }
  }

  // Interactive lyrics: Click to jump to specific timestamp
  const handleLyricClick = async (lyric, index) => {
    if (lyric.start_time !== undefined) {
      console.log(`🎵 Jumping to lyric ${index}: "${lyric.text}" at ${lyric.start_time}s (preserving playback state: ${localIsPlaying})`)

      try {
        // Provide immediate visual feedback
        setCurrentLyricIndex(index)

        // Preserve current playback state - don't change play/pause when clicking lyrics
        await handleSeek(lyric.start_time, localIsPlaying, { reason: 'lyric-click-seek' })
        usePlaybackStore.getState().seek(roomId, lyric.start_time, localIsPlaying).catch((error) => {
          console.error('🎵 Failed to sync lyric seek:', error)
          toast.error(getPlaybackControlErrorMessage(error, 'Seek applied locally. Room sync may lag briefly.'))
        })
        console.log(`🎵 Seeked to ${lyric.start_time}s successfully, playback state preserved`)
      } catch (error) {
        console.error('🎵 Failed to seek to lyric position:', error)
        toast.error(getPlaybackControlErrorMessage(error, 'Failed to seek'))
      }
    }
  }

  // Auto-scroll to current lyric (Apple Music style)
  const scrollToCurrentLyric = (index) => {
    if (!lyricsContainerRef.current) return

    const container = lyricsContainerRef.current
    const anchorSelector = '[data-lyric-index][data-scroll-anchor="true"]'
    const directAnchor = container.querySelector(
      `[data-lyric-index="${index}"][data-scroll-anchor="true"]`
    )
    const fallbackAnchor = container.querySelector(`[data-lyric-index="${index}"]`)

    let lyricElement = directAnchor || null

    if (!lyricElement) {
      const anchorElements = Array.from(container.querySelectorAll(anchorSelector))
      if (anchorElements.length > 0) {
        const targetIndex = Number(index)
        const anchorsWithIndex = anchorElements
          .map((element) => ({
            element,
            lyricIndex: Number(element.getAttribute('data-lyric-index'))
          }))
          .filter((item) => Number.isFinite(item.lyricIndex))
          .sort((a, b) => a.lyricIndex - b.lyricIndex)

        if (Number.isFinite(targetIndex) && anchorsWithIndex.length > 0) {
          const nextAnchor = anchorsWithIndex.find((item) => item.lyricIndex >= targetIndex)
          lyricElement = nextAnchor?.element || anchorsWithIndex[anchorsWithIndex.length - 1]?.element || null
        } else {
          lyricElement = anchorsWithIndex[0]?.element || null
        }
      }
    }

    lyricElement = lyricElement || fallbackAnchor
    if (!lyricElement) return

    const scrollTargetIndex = Number(lyricElement.getAttribute('data-lyric-index'))
    if (scrollTargetIndex === 0) {
      container.scrollTo({
        top: 0,
        behavior: 'auto'
      })
      return
    }

    lyricElement.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest'
    })
  }

  const handleAutoScrollToggle = () => {
    const nextAutoScrollEnabled = !autoScrollEnabled
    setAutoScrollEnabled(nextAutoScrollEnabled)

    if (nextAutoScrollEnabled && audioTracksLoaded) {
      requestAnimationFrame(() => scrollToCurrentLyric(currentLyricIndex))
    }
  }

  const handleProgressBarClick = async (e) => {
    if (!currentSong || !currentSong.duration || audioError) return

    const progressBar = e.currentTarget
    const rect = progressBar.getBoundingClientRect()
    const clickX = e.clientX - rect.left
    const percentage = clickX / rect.width
    const newPosition = percentage * currentSong.duration

    console.log(`🎯 Progress bar clicked: seeking to position: ${newPosition.toFixed(2)}s`)

    try {
      // Progress bar clicks should always resume playback (traditional behavior)
      await handleSeek(newPosition, true, { reason: 'progress-bar-seek' })
      usePlaybackStore.getState().seek(roomId, newPosition, true).catch((error) => {
        console.error('🎯 Failed to sync seek to position:', error)
        toast.error(getPlaybackControlErrorMessage(error, 'Seek applied locally. Room sync may lag briefly.'))
      })

      console.log(`🎯 Progress bar seek to ${newPosition.toFixed(2)}s successful`)
    } catch (error) {
      console.error('🎯 Failed to seek to position:', error)
      toast.error(getPlaybackControlErrorMessage(error, 'Failed to seek'))
    }
  }

  // Add click listener for user gesture when needed
  useEffect(() => {
    if (needsUserGesture) {
      const handleClick = async () => {
        await handleUserGesture();
      };

      document.addEventListener('click', handleClick, { once: true });
      document.addEventListener('keydown', handleClick, { once: true });
      document.addEventListener('touchstart', handleClick, { once: true });

      return () => {
        document.removeEventListener('click', handleClick);
        document.removeEventListener('keydown', handleClick);
        document.removeEventListener('touchstart', handleClick);
      };
    }
  }, [needsUserGesture, handleUserGesture]);

  // Keyboard controls for TV page
  useEffect(() => {
    const handleKeydown = async (e) => {
      // Ignore keyboard events if audio gesture is needed or no current song
      if (needsUserGesture || !currentSong) return;

      // Prevent default behavior for the keys we handle
      if (e.code === 'Space' || e.code === 'ArrowLeft' || e.code === 'ArrowRight') {
        e.preventDefault();
      }

      switch (e.code) {
        case 'Space':
          // Toggle play/pause
          if (localIsPlaying) {
            await handlePause();
          } else {
            await handlePlay();
          }
          break;

        case 'ArrowRight':
          // Skip to next song with instant transition (optimistic updates)
          console.log('🎵 Keyboard next - optimistic transition...');
          await handleNext();
          break;

        case 'ArrowLeft':
          // Skip to previous song with instant transition (optimistic updates)  
          console.log('🎵 Keyboard previous - optimistic transition...');
          await handlePrevious();
          break;

        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeydown);

    return () => {
      document.removeEventListener('keydown', handleKeydown);
    };
  }, [needsUserGesture, currentSong, localIsPlaying, handlePause, handlePlay, handleNext, handlePrevious]);

  return (
    <div className="tv-background min-h-[100dvh] relative overflow-hidden">
      {/* Audio Engine with Real-time Pitch Shifting (no HTML5 audio needed) */}

      {/* Animated gradient background - always present */}
      <CanvasAnimatedBackground
        colors={currentSong ? animatedBackgroundColors : null}
        brightness={backgroundBrightness}
        animationPaused={animationPaused}
      />
      <FloatingParticles count={40} colors={particleColors} />

      <EmojiRainOverlay events={emojiEvents} />

      {/* User Gesture Required Overlay */}
      {needsUserGesture && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md mx-4 text-center">
            <div className="text-6xl mb-4">🎵</div>
            <h3 className="text-xl font-bold mb-2">Audio Ready to Play</h3>
            <p className="text-gray-600 mb-4">
              Click anywhere to enable audio playback
            </p>
            <p className="text-sm text-gray-500">
              This is required by your browser's audio policy
            </p>
          </div>
        </div>
      )}

      {/* Top Navigation Bar */}
      <div
        className="absolute top-0 left-0 right-0 flex justify-between items-start z-20"
        style={{
          paddingTop: 'calc(1.5rem + env(safe-area-inset-top, 0px))',
          paddingLeft: 'calc(1.5rem + env(safe-area-inset-left, 0px))',
          paddingRight: 'calc(1.5rem + env(safe-area-inset-right, 0px))',
          paddingBottom: '1.5rem'
        }}
      >
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleFullscreenToggle}
            aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            aria-hidden={!controlsVisible}
            tabIndex={controlsVisible ? 0 : -1}
            title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur-sm transition-all duration-500 hover:bg-white/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 ${controlsVisible ? 'translate-y-0 opacity-100' : 'pointer-events-none -translate-y-2 opacity-0'}`}
          >
            {isFullscreen ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
          </button>
          <ConnectionStatus />
        </div>

        {/* QR Code Display */}
        <div className="flex justify-center overflow-visible relative z-30">
          <QRCodeComponent roomCode={roomCode} size={80} className="bg-white/10 backdrop-blur-sm" />
        </div>
      </div>

      {/* Song Title & Artist - True Center */}
      {currentSong && (
        <div className="absolute top-6 left-1/2 transform -translate-x-1/2 z-20 text-center">
          <h1
            className="font-bold text-white lyrics-container lyrics-text mb-1"
            style={{
              color: 'white !important',
              fontSize: '30px',
              lineHeight: 1.2
            }}
          >
            {currentSong.title}
          </h1>
          {currentSong.artist && (
            <p
              className="text-white/80 lyrics-container lyrics-text text-base font-bold"
              style={{
                color: 'rgba(255, 255, 255, 0.8) !important',
                lineHeight: 1.3
              }}
            >
              {currentSong.artist}
            </p>
          )}
        </div>
      )}

      {/* Main Content */}
      <div className="flex flex-col h-[100dvh] relative z-10">
        {/* Song and Lyrics Display */}
        <SongDisplay currentSong={currentSong} roomCode={roomCode} controlsVisible={controlsVisible}>
          <LyricsDisplay
            lyrics={lyrics}
            currentLyricIndex={currentLyricIndex}
            audioError={audioError}
            audioLoading={audioLoading}
            onLyricClick={handleLyricClick}
            lyricsContainerRef={lyricsContainerRef}
            formatTime={formatTime}
            showLyrics={showLyrics}
            showTranslation={showTranslation}
            showRomanization={showRomanization}
            wordLevelTranscription={fullCurrentSong?.wordLevelTranscription}
            currentTime={currentTime}
            isPlaying={localIsPlaying}
            forceLineMode={forceLineMode}
            autoScrollEnabled={autoScrollEnabled}
            enableCountIn
            countInGapThresholdSeconds={COUNT_IN_GAP_THRESHOLD_SECONDS}
            countInMinDurationSeconds={2}
          />
        </SongDisplay>

        {/* Unified Controls Container with Gradient */}
        <div className={`relative flex-shrink-0 transition-all duration-500 ${controlsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-full'}`}>
          {/* Strong bottom gradient for control visibility - restoring original strength */}
          <div className={`absolute inset-0 -top-20 bg-gradient-to-t from-black/80 via-black/50 to-transparent transition-opacity duration-500 ${controlsVisible ? 'opacity-100' : 'opacity-0'}`} />

          {/* Controls Content */}
          <div className="relative z-10">
            {/* Playback Controls */}
            <PlaybackControls
              currentSong={currentSong}
              currentTime={currentTime}
              localIsPlaying={localIsPlaying}
              audioError={audioError}
              audioEngineReady={audioEngineReady}
              audioTracksLoaded={audioTracksLoaded}
              onPlay={handlePlay}
              onPause={handlePause}
              onNext={handleNext}
              onPrevious={handlePrevious}
              onProgressBarClick={handleProgressBarClick}
              formatTime={formatTime}
              getProgressPercentage={getProgressPercentage}
              queue={queue}
              recentUpdate={recentUpdate}
              controlsVisible={controlsVisible}
            />
          </div>
        </div>
      </div>

      {/* Audio Settings Modal */}
      {currentSong && (
        <AudioControlsModal
          pitchShift={pitchShift}
          onPitchShiftChange={handlePitchShiftChange}
          pitchShiftEnabled={pitchShiftEnabled}
          onPitchShiftEnabledChange={handlePitchShiftEnabledChange}
          vocalVolume={vocalVolume}
          accompanimentVolume={accompanimentVolume}
          drumsVolume={drumsVolume}
          vocalMuted={vocalMuted}
          accompanimentMuted={accompanimentMuted}
          drumsMuted={drumsMuted}
          onVocalVolumeChange={handleVocalVolumeChange}
          onAccompanimentVolumeChange={handleAccompanimentVolumeChange}
          onDrumsVolumeChange={handleDrumsVolumeChange}
          onVocalMuteToggle={toggleVocalMute}
          onAccompanimentMuteToggle={toggleAccompanimentMute}
          onDrumsMuteToggle={toggleDrumsMute}
          audioEngineReady={audioEngineReady}
          audioEngineError={audioEngineError}
          getKeyDisplay={getKeyDisplay}
          queue={queue}
          showLyrics={showLyrics}
          showTranslation={showTranslation}
          showRomanization={showRomanization}
          onShowLyricsToggle={() => setShowLyrics(!showLyrics)}
          onShowTranslationToggle={() => setShowTranslation(!showTranslation)}
          onShowRomanizationToggle={() => setShowRomanization(!showRomanization)}
          forceLineMode={forceLineMode}
          onForceLineModeToggle={() => setForceLineMode(!forceLineMode)}
          autoScrollEnabled={autoScrollEnabled}
          onAutoScrollToggle={handleAutoScrollToggle}
          backgroundBrightness={backgroundBrightness}
          animationPaused={animationPaused}
          onBackgroundBrightnessChange={setBackgroundBrightness}
          onAnimationPausedToggle={() => setAnimationPaused(!animationPaused)}
          controlsVisible={controlsVisible}
        />
      )}

      {/* Up Next Popup */}
      <UpNextPopup
        nextSong={upNextSong}
        onNext={handleNext}
        visible={showUpNextPopup}
        remainingSeconds={upNextRemainingSeconds}
        controlsVisible={controlsVisible}
        currentSong={currentSong}
      />
    </div>
  )
}

export default TVPage
